import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACCOUNT_WORKFLOW_STATUSES,
  AccountWorkflowStatus,
  AccountStatusCounts,
  ReportedAccountStatus,
  Skeleton,
  withStyles,
} from 'vopay';
import { BodyText, Card } from 'UI';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import type { PartnerAccountStatusFilter } from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';
import { TERMINAL_STATUSES } from './onboardingPipeline';
import styles from './AccountsOverview.module.scss';

const cn = withStyles(styles);

/** Everything but the terminal statuses: what an account passes through on its way to one of them. */
const ONBOARDING_STATUSES = ACCOUNT_WORKFLOW_STATUSES.filter((status) => !TERMINAL_STATUSES.includes(status));

/** Membership test for "is this a status this frontend knows about", over the whole vocabulary. */
const KNOWN_STATUSES: ReadonlySet<string> = new Set<string>(ACCOUNT_WORKFLOW_STATUSES);

type AccountStatusSummaryCardProps = {
  fetching: boolean;
  numTotalAccounts: number;
  /**
   * Partial while `fetching`; every documented status is present once the summary has responded.
   * The backend keys these off the live `VIOAccounts.AccountStatus` enum, so the response can also
   * carry a status newer than `AccountWorkflowStatus` — see `unrecognizedStages` below.
   */
  statusCounts: Partial<AccountStatusCounts>;
  /**
   * The tiles, in order (see `getAccountStatusTiles`): the partner's pipeline, other populated
   * onboarding statuses, then the terminal statuses. The same list drives the list's Status filter.
   */
  stages: AccountWorkflowStatus[];
  /** The status the list is filtered to; its tile renders selected. Empty (or 'all') = no filter. */
  selectedStatus: PartnerAccountStatusFilter;
  /** A tile click (ID-13981): the host filters the list to the status, or clears it when it is the selected one. */
  onStatusClick: (status: ReportedAccountStatus) => void;
};

export default function AccountStatusSummaryCard({
  fetching,
  numTotalAccounts,
  statusCounts,
  stages,
  selectedStatus,
  onStatusClick,
}: AccountStatusSummaryCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountsOverview' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();
  // One radio group per card, so two cards on a page never share a selection.
  const radioGroupName = useId();

  const numActive = statusCounts.Active ?? 0;

  // Widened only to read keys the union does not name. Everything derived from the vocabulary below
  // stays typed; this is the one place the raw response shape is acknowledged.
  const counts: Record<string, number | undefined> = statusCounts;

  /**
   * Statuses the response carried that this frontend has no name for yet — a status added to the
   * backend enum before `AccountWorkflowStatus` catches up. They are deliberately kept out of the
   * typed lists: `resolveStatusLabel` renders them from their raw value, and showing them is what
   * keeps the card's promise that a stage holding accounts is never hidden.
   */
  const unrecognizedStages: ReportedAccountStatus[] = Object.keys(counts).filter(
    (status) => !KNOWN_STATUSES.has(status) && (counts[status] ?? 0) > 0,
  );

  const numOnboarding =
    ONBOARDING_STATUSES.reduce((sum, status) => sum + (statusCounts[status] ?? 0), 0) +
    unrecognizedStages.reduce((sum, status) => sum + (counts[status] ?? 0), 0);

  return (
    <Card variant="section" className={cn('card', 'summaryCard')} data-testid="accountStatusSummaryCard">
      <div className={cn('totalBlock')}>
        {fetching ? <Skeleton width={40} height={46} /> : <span className={cn('total')}>{numTotalAccounts}</span>}
        {/* Body/16/Regular, Text/Secondary75 */}
        <BodyText className={cn('secondaryText')}>{t('accounts', { defaultValue: 'Accounts' })}</BodyText>
      </div>

      <div className={cn('divider')} />

      <div className={cn('breakdown')}>
        {fetching ? (
          <Skeleton width={200} height={18} />
        ) : (
          <div className={cn('legend')}>
            {/* Body/13/Medium */}
            <BodyText size="sm" weight="md" className={cn('legendItem', 'legendActive')}>
              {t('active', { defaultValue: '{{count}} active', count: numActive })}
            </BodyText>

            <BodyText size="sm" weight="md" className={cn('legendItem', 'legendOnboarding')}>
              {t('inOnboarding', { defaultValue: '{{count}} in onboarding', count: numOnboarding })}
            </BodyText>
          </div>
        )}

        {/* Only one status can filter the list at a time, so the tiles are a radio group (ID-13981). */}
        <div
          role="radiogroup"
          aria-label={t('statusFilterLabel', { defaultValue: 'Filter accounts by status' })}
          className={cn('tiles')}
        >
          {[...stages, ...unrecognizedStages].map((stage) => {
            const count = counts[stage] ?? 0;
            const isSelected = stage === selectedStatus;

            // A tile is a <label>, whose content must be phrasing, so BodyText's block <div>s can't be
            // used: its Body/13 text styles live in the module SCSS (same exception the figma-to-vopay
            // skill makes for option-card buttons).
            return (
              <label key={stage} className={cn('tile', count === 0 && 'tileEmpty', isSelected && 'tileSelected')}>
                <input
                  type="radio"
                  name={radioGroupName}
                  value={stage}
                  checked={isSelected}
                  className={cn('tileRadio')}
                  onChange={() => onStatusClick(stage)}
                  // A checked radio fires no change when it is clicked (or Space-pressed) again, only a
                  // click, so that click is what clears the filter (the ticket's "click the active
                  // stage again"); an unchecked one is handled by onChange alone.
                  onClick={() => {
                    if (isSelected) onStatusClick(stage);
                  }}
                />

                <span className={cn('tileLabel')}>{resolveStatusLabel(stage)}</span>
                {fetching ? <Skeleton width={12} height={18} /> : <span className={cn('tileCount')}>{count}</span>}
              </label>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
